'use client';

import Icon from '@/components/ui/AppIcon';

interface UserObligationsSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function UserObligationsSection({ isExpanded, onToggle }: UserObligationsSectionProps) {
  const obligations = [
    {
      title: 'Property Care Responsibilities',
      icon: 'HomeIcon',
      items: [
        'Maintain cleanliness and orderliness of the apartment during stay',
        'Report any damages or maintenance issues immediately to management',
        'Use all appliances and amenities as per manufacturer guidelines',
        'Ensure proper waste disposal and recycling practices',
        'Return all keys, access cards, and apartment items upon checkout'
      ]
    },
    {
      title: 'Conduct Standards',
      icon: 'UserGroupIcon',
      items: [
        'Respect other guests and maintain peaceful environment',
        'Comply with noise restrictions between 10 PM and 8 AM',
        'No illegal activities or substances permitted on premises',
        'Dress appropriately in common areas and public spaces',
        'Follow all safety protocols and emergency procedures'
      ]
    },
    {
      title: 'Visitor Policies',
      icon: 'UsersIcon',
      items: [
        'Register all visitors at reception before entry',
        'Visitors allowed only between 9 AM and 9 PM unless pre-approved',
        'Maximum 2 visitors per apartment at any given time',
        'Guests responsible for visitor conduct and any damages caused',
        'Overnight visitors require prior management approval'
      ]
    },
    {
      title: 'Compliance with Local Regulations',
      icon: 'ShieldCheckIcon',
      items: [
        'Provide valid government-issued ID for all guests during check-in',
        'Comply with local laws, ordinances, and regulations',
        'Cooperate with security checks and verification procedures',
        'Adhere to fire safety regulations and evacuation procedures',
        'Report any suspicious activities to management immediately'
      ]
    }
  ];

  return (
    <section id="user-obligations" className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="UserCircleIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Section 2</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              User Obligations
            </h2>
            <p className="text-lg text-text-secondary">
              Guest responsibilities covering property care, conduct standards, visitor policies, and compliance with local regulations.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-premium border border-border overflow-hidden">
            <button
              onClick={onToggle}
              className="w-full px-6 py-6 flex items-center justify-between hover:bg-muted transition-colors duration-300"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="ClipboardDocumentListIcon" size={24} className="text-primary" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-semibold text-text-primary">View Guest Obligations</h3>
                  <p className="text-sm text-text-secondary mt-1">Click to expand all user responsibilities and obligations</p>
                </div>
              </div>
              <Icon 
                name={isExpanded ? "ChevronUpIcon" : "ChevronDownIcon"} 
                size={24} 
                className="text-text-secondary flex-shrink-0" 
              />
            </button>

            {isExpanded && (
              <div className="px-6 pb-6 space-y-6 animate-scale-in">
                {obligations.map((obligation, index) => (
                  <div key={index} className="border-t border-border pt-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                        <Icon name={obligation.icon as any} size={20} className="text-accent" />
                      </div>
                      <h4 className="text-lg font-semibold text-text-primary">{obligation.title}</h4>
                    </div>
                    <ul className="space-y-3 ml-13">
                      {obligation.items.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon name="CheckCircleIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-text-secondary">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}